import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Plus, Trash2, Upload, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, CalendarDays, Target, DollarSign, AlertCircle, Clock, FileText, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';
import { PieChart, Pie, Cell, Tooltip as RTooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';

const SITUACOES = {
  a_confirmar: { label: 'À confirmar', cor: 'bg-amber-100 text-amber-800' },
  opcao: { label: 'Opção', cor: 'bg-sky-100 text-sky-800' },
  reservado: { label: 'Reservado', cor: 'bg-indigo-100 text-indigo-800' },
  confirmado: { label: 'Confirmado', cor: 'bg-emerald-100 text-emerald-800' },
  adiado: { label: 'Adiado', cor: 'bg-stone-200 text-stone-700' },
  nao_executada: { label: 'Não executada (concorrente)', cor: 'bg-red-600 text-white font-semibold' },
  cancelado: { label: 'Cancelado', cor: 'bg-stone-100 text-stone-500 line-through' },
};
const PAGAMENTOS = {
  nao_faturado: 'Não faturado',
  a_cobrar: 'A COBRAR',
  pago_30: 'Pago 30%',
  pago_50: 'Pago 50%',
  pago_50_cobrar: 'Pago 50% / cobrar 50%',
  pago_100: 'Pago 100%',
};
const ETAPAS_CRM = [
  ['prospeccao', 'Prospecção'], ['contato', 'Contato feito'], ['proposta', 'Proposta enviada'],
  ['negociacao', 'Negociação'], ['fechado', 'Fechado ✓'], ['perdido', 'Perdido'],
];
const INATIVOS = ['cancelado', 'nao_executada', 'adiado'];
const GRAF_SITS = ['confirmado', 'reservado', 'a_confirmar', 'opcao', 'adiado', 'cancelado'];
const STATUS_PROSP = {
  prospeccao: 'Prospecção', contato_feito: 'Contato feito', aguardando_retorno: 'Aguardando retorno',
  ja_orcado: 'Já possui orçamento', nao_prospectar: 'Não prospectar',
};
const normStatus = (s) => ({ nao_abordado: 'prospeccao', em_contato: 'contato_feito', proposta: 'aguardando_retorno', fechado: 'ja_orcado', descartado: 'nao_prospectar' }[s] || s || 'prospeccao');
const ETAPAS_PROSP = [['prospeccao', 'Prospecção'], ['contato_feito', 'Contato feito'], ['aguardando_retorno', 'Aguardando retorno']];
const SERVICOS_OPCOES = ['Sonorização', 'Painel de LED / Vídeo', 'Iluminação', 'Estruturas', 'Tradução simultânea', 'Informática', 'Streaming', 'Gravação', 'Outros'];
const STATUS_HEX = {
  a_confirmar: '#f59e0b', opcao: '#0ea5e9', reservado: '#6366f1', confirmado: '#10b981',
  adiado: '#78716c', nao_executada: '#f43f5e', cancelado: '#a8a29e',
};
const SOLIC_STATUS = {
  a_orcar: { label: 'Faltando enviar orçamento', cor: 'bg-amber-100 text-amber-800 border-amber-300' },
  orcamento_enviado: { label: 'Orçamento enviado', cor: 'bg-sky-100 text-sky-800 border-sky-300' },
  convertido: { label: 'Convertido em OS ✓', cor: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  descartado: { label: 'Descartado', cor: 'bg-stone-100 text-stone-500 border-stone-200' },
};
const fmtBRL = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (d) => (d ? d.split('-').reverse().join('/') : '-');
const recebTotal = (o) => (o.recebimentos?.length ? o.recebimentos.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0) : (o.valor_recebido || 0));
const valorEfetivo = (o) => (o.cortesia ? 0 : (o.total || 0));
const pendenteDe = (o) => (o.cortesia ? 0 : Math.max(0, (o.total || 0) - recebTotal(o) - (o.retencao || 0)));

const LARGURAS_PADRAO = { num: 72, data: 92, total: 105, situacao: 135, pagamento: 135, nf: 95, contr: 55, recebido: 105, pendente: 105, obs: 150, contatos: 80, acoes: 64 };

function ThR({ k, colW, setColW, className = '', children }) {
  const iniciar = (e) => {
    e.preventDefault(); e.stopPropagation();
    const th = e.currentTarget.closest('th');
    const x0 = e.clientX, w0 = th.offsetWidth;
    const mover = (ev) => setColW(p => ({ ...p, [k]: Math.max(40, w0 + ev.clientX - x0) }));
    const soltar = () => { window.removeEventListener('mousemove', mover); window.removeEventListener('mouseup', soltar); };
    window.addEventListener('mousemove', mover); window.addEventListener('mouseup', soltar);
  };
  const w = colW[k] ?? LARGURAS_PADRAO[k];
  return (
    <th className={`relative ${className}`} style={w ? { width: w } : undefined}>
      {children}
      <span onMouseDown={iniciar} onDoubleClick={() => setColW(p => { const n = { ...p }; delete n[k]; return n; })}
        title="Arraste para redimensionar • duplo clique restaura"
        className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none hover:bg-sky-400/60" data-testid={`resize-${k}`} />
    </th>
  );
}

function ProspCard({ e, admin, onEdit, onReload }) {
  const [exp, setExp] = useState(false);
  const st = normStatus(e.status_abordagem);
  const hj = new Date().toISOString().slice(0, 10);
  const aberto = ['prospeccao', 'contato_feito', 'aguardando_retorno'].includes(st);
  const atrasado = e.proximo_followup && e.proximo_followup < hj && aberto;
  const mudar = async (campos) => { await api.put(`/eventos-cidade/${e.id}`, campos); onReload(); };
  return (
    <div className={`rounded border bg-background p-2 text-xs space-y-1 ${atrasado ? 'border-red-400' : ''}`} data-testid={`prosp-${e.id}`}>
      <div className="flex justify-between gap-1">
        <button onClick={() => setExp(!exp)} className="font-medium text-left" data-testid={`prosp-nome-${e.id}`}>★ {e.nome}</button>
        {admin && <button onClick={async () => { if (window.confirm('Excluir prospecção?')) { await api.delete(`/eventos-cidade/${e.id}`); onReload(); } }} data-testid={`prosp-del-${e.id}`}><Trash2 className="w-3 h-3 text-stone-400" /></button>}
      </div>
      <div className="text-muted-foreground">{fmtData(e.data)}{e.data_fim ? ` a ${fmtData(e.data_fim)}` : ''}{e.hora_inicio ? ` • ${e.hora_inicio}${e.hora_fim ? '–' + e.hora_fim : ''}` : ''}</div>
      {(e.local || e.organizador) && <div className="text-muted-foreground truncate" title={`${e.local || ''} ${e.organizador || ''}`}>{e.local}{e.local && e.organizador ? ' • ' : ''}{e.organizador}</div>}
      {e.os_vinculada && <div className="text-emerald-700 dark:text-emerald-400 font-semibold" data-testid={`prosp-os-${e.id}`}>OS {e.os_vinculada} vinculada</div>}
      {e.potencial && <span className={`rounded-full px-1.5 text-[10px] ${e.potencial === 'alto' ? 'bg-emerald-100 text-emerald-700' : e.potencial === 'medio' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600'}`}>potencial {e.potencial}</span>}
      {e.proximo_followup && <div className={atrasado ? 'text-red-600 font-semibold' : 'text-muted-foreground'}>Follow-up: {fmtData(e.proximo_followup)}{atrasado ? ' (vencido)' : ''}</div>}
      {exp && (
        <div className="pt-1 border-t space-y-0.5 text-muted-foreground" data-testid={`prosp-detalhe-${e.id}`}>
          {e.contato_nome && <div>Contato: {e.contato_nome}{e.cargo ? ` (${e.cargo})` : ''}</div>}
          {(e.telefone || e.whatsapp) && <div>{e.telefone}{e.telefone && e.whatsapp ? ' • ' : ''}{e.whatsapp ? `WhatsApp: ${e.whatsapp}` : ''}</div>}
          {e.email && <div>{e.email}</div>}
          {e.site && <a href={e.site.startsWith('http') ? e.site : `https://${e.site}`} target="_blank" rel="noreferrer" className="text-sky-600 dark:text-sky-400 underline break-all">{e.site}</a>}
          {e.link_fonte && <div className="break-all">Fonte: {e.link_fonte}</div>}
          {e.endereco && <div>{e.endereco}</div>}
          {e.servicos?.length > 0 && <div>Serviços: {e.servicos.join(', ')}</div>}
          {e.descricao && <div>{e.descricao}</div>}
          {e.ultimo_contato && <div>Último contato: {fmtData(e.ultimo_contato)}</div>}
          {e.obs && <div>Obs: {e.obs}</div>}
        </div>
      )}
      <div className="flex flex-wrap gap-1 pt-1 items-center">
        <select value={st} onChange={ev => mudar({ status_abordagem: ev.target.value })} className="text-[11px] rounded border px-1 py-0.5 bg-background" data-testid={`prosp-status-${e.id}`}>
          {Object.entries(STATUS_PROSP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={() => mudar({ ultimo_contato: hj })} className="rounded border px-1.5 py-0.5" title="Registrar contato feito hoje" data-testid={`prosp-contato-${e.id}`}>Contato hoje</button>
        {!e.os_vinculada && <button onClick={async () => { const n = window.prompt('Número da OS a vincular:'); if (n) { await mudar({ os_vinculada: n.trim(), status_abordagem: 'ja_orcado' }); toast.success(`OS ${n.trim()} vinculada — o card passa a seguir a situação da OS`); } }} className="rounded border px-1.5 py-0.5 text-emerald-700 dark:text-emerald-400" data-testid={`prosp-vincular-${e.id}`}>Vincular OS</button>}
        <button onClick={onEdit} className="rounded border px-1.5 py-0.5" data-testid={`prosp-editar-${e.id}`}>Editar</button>
      </div>
    </div>
  );
}

function FormProspeccao({ inicial, onSalvo, onCancel }) {
  const [f, setF] = useState({
    nome: '', data: '', data_fim: '', hora_inicio: '', hora_fim: '', local: '', endereco: '', organizador: '',
    contato_nome: '', cargo: '', telefone: '', whatsapp: '', email: '', site: '', link_fonte: '', descricao: '',
    potencial: '', servicos: [], status_abordagem: 'prospeccao', ultimo_contato: '', proximo_followup: '', obs: '', os_vinculada: '',
    ...inicial,
  });
  const [salvando, setSalvando] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const campo = (k, ph, type = 'text') => <Input type={type} placeholder={ph} title={ph} value={f[k] || ''} onChange={e => set(k, e.target.value)} className="h-8 text-xs" data-testid={`ev-${k}`} />;
  const salvar = async () => {
    if (!f.nome || !f.data || !(f.local || f.organizador)) { toast.error('Preencha: nome do evento, data inicial e local ou organizador'); return; }
    setSalvando(true);
    try {
      const { _sugId, id, company_id, ...dados } = f;
      if (id) { await api.put(`/eventos-cidade/${id}`, dados); toast.success('Prospecção atualizada'); }
      else {
        await api.post('/eventos-cidade', dados);
        if (_sugId) await api.put(`/eventos-sugestoes/${_sugId}`, { status: 'cadastrado' });
        toast.success('Prospecção cadastrada com sucesso');
      }
      onSalvo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar prospecção');
    } finally { setSalvando(false); }
  };
  return (
    <Card data-testid="form-prospeccao"><CardContent className="p-3 space-y-2 text-xs">
      <div className="font-semibold text-sm">{f.id ? 'Editar prospecção' : 'Nova prospecção'}</div>
      {campo('nome', 'Nome do evento *')}
      <div className="grid grid-cols-2 gap-2">
        <div><label className="text-[10px] text-muted-foreground">Data inicial *</label>{campo('data', '', 'date')}</div>
        <div><label className="text-[10px] text-muted-foreground">Data final</label>{campo('data_fim', '', 'date')}</div>
        <div><label className="text-[10px] text-muted-foreground">Horário inicial</label>{campo('hora_inicio', '', 'time')}</div>
        <div><label className="text-[10px] text-muted-foreground">Horário final</label>{campo('hora_fim', '', 'time')}</div>
      </div>
      {campo('local', 'Local')}
      {campo('endereco', 'Endereço')}
      {campo('organizador', 'Organizador / Realizador')}
      <div className="grid grid-cols-2 gap-2">
        {campo('contato_nome', 'Nome do contato')}
        {campo('cargo', 'Cargo/Função')}
        {campo('telefone', 'Telefone')}
        {campo('whatsapp', 'WhatsApp')}
        {campo('email', 'E-mail')}
        {campo('site', 'Site oficial')}
      </div>
      {campo('link_fonte', 'Link/fonte da informação')}
      <Textarea rows={2} placeholder="Descrição / dados relevantes" value={f.descricao || ''} onChange={e => set('descricao', e.target.value)} className="text-xs" data-testid="ev-descricao" />
      <div className="flex flex-wrap gap-2 items-center">
        <label className="text-[10px] text-muted-foreground uppercase">Potencial:</label>
        {['alto', 'medio', 'baixo'].map(p => (
          <button key={p} onClick={() => set('potencial', f.potencial === p ? '' : p)} data-testid={`ev-potencial-${p}`}
            className={`rounded-full px-2 py-0.5 border text-xs capitalize ${f.potencial === p ? (p === 'alto' ? 'bg-emerald-100 text-emerald-700 border-emerald-400' : p === 'medio' ? 'bg-amber-100 text-amber-700 border-amber-400' : 'bg-stone-100 text-stone-600 border-stone-400') : ''}`}>{p === 'medio' ? 'Médio' : p}</button>
        ))}
      </div>
      <div>
        <label className="text-[10px] text-muted-foreground uppercase">Serviços potenciais:</label>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
          {SERVICOS_OPCOES.map(s => (
            <label key={s} className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={(f.servicos || []).includes(s)} data-testid={`ev-servico-${s.split(' ')[0].toLowerCase()}`}
                onChange={e => set('servicos', e.target.checked ? [...(f.servicos || []), s] : (f.servicos || []).filter(x => x !== s))} className="w-3.5 h-3.5 accent-sky-600" />{s}
            </label>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className="text-[10px] text-muted-foreground">Situação</label>
          <select value={normStatus(f.status_abordagem)} onChange={e => set('status_abordagem', e.target.value)} className="w-full h-8 rounded border px-1 bg-background" data-testid="ev-status">
            {Object.entries(STATUS_PROSP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div><label className="text-[10px] text-muted-foreground">OS vinculada (nº)</label>{campo('os_vinculada', 'ex: 018147')}</div>
        <div><label className="text-[10px] text-muted-foreground">Último contato</label>{campo('ultimo_contato', '', 'date')}</div>
        <div><label className="text-[10px] text-muted-foreground">Próximo follow-up</label>{campo('proximo_followup', '', 'date')}</div>
      </div>
      <Textarea rows={2} placeholder="Observações" value={f.obs || ''} onChange={e => set('obs', e.target.value)} className="text-xs" data-testid="ev-obs" />
      <div className="flex gap-2">
        <Button size="sm" disabled={salvando} onClick={salvar} data-testid="salvar-evento">{salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancelar</Button>
      </div>
    </CardContent></Card>
  );
}

function ObsAdmInput({ o }) {
  const [v, setV] = useState(o.obs_adm || '');
  const [st, setSt] = useState('idle');
  const timer = useRef(null);
  const vRef = useRef(v); vRef.current = v;
  const salvoRef = useRef(o.obs_adm || '');
  const salvar = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const val = vRef.current;
    if (val === salvoRef.current) return;
    setSt('salvando');
    try {
      await api.put(`/orcamentos/${o.id}`, { obs_adm: val });
      salvoRef.current = val;
      setSt('salvo');
      setTimeout(() => setSt(s => (s === 'salvo' ? 'idle' : s)), 2500);
    } catch (err) {
      setSt('erro');
      toast.error(`OBS da OS ${o.numero} NÃO foi salva — ${err.response?.data?.detail || 'verifique a conexão e tente de novo'}`);
    }
  }, [o.id, o.numero]);
  const salvarRef = useRef(salvar); salvarRef.current = salvar;
  useEffect(() => () => { salvarRef.current(); }, []);
  return (
    <div className="relative">
      <input type="text" value={v} placeholder="obs..."
        onChange={e => { setV(e.target.value); setSt('digitando'); if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => salvarRef.current(), 800); }}
        onBlur={salvar}
        onKeyDown={e => { if (e.key === 'Enter') salvar(); }}
        data-testid={`obsadm-${o.numero}`}
        className={`w-full h-7 text-xs rounded border px-1 bg-background ${st === 'erro' ? 'border-red-500' : ''}`} />
      {st !== 'idle' && (
        <span data-testid={`obsadm-status-${o.numero}`} className={`absolute -bottom-3 right-0 text-[9px] leading-none ${st === 'erro' ? 'text-red-600 font-bold' : st === 'salvo' ? 'text-emerald-600' : 'text-muted-foreground animate-pulse'}`}>
          {st === 'salvando' ? 'Salvando...' : st === 'salvo' ? '✓ Salvo' : st === 'erro' ? 'ERRO — não salvo' : '...'}
        </span>
      )}
    </div>
  );
}

const TIPOS_CONTATO = [['ligacao', 'Ligação'], ['whatsapp', 'WhatsApp'], ['email', 'E-mail'], ['visita', 'Visita'], ['retorno', 'Retorno recebido'], ['tentativa', 'Tentativa de contato'], ['obs_comercial', 'Obs. comercial']];
const TIPO_LABEL = Object.fromEntries(TIPOS_CONTATO);

function ContatosOrc({ o, onAdd, onFollowup, admin, onDelContato }) {
  const [txt, setTxt] = useState('');
  const [tipo, setTipo] = useState('');
  const [fuOn, setFuOn] = useState(false);
  const [fu, setFu] = useState({ data: '', hora: '' });
  const [sending, setSending] = useState(false);
  const hj = new Date().toISOString().slice(0, 10);
  const enviar = async () => {
    if (!txt.trim() || sending) return;
    setSending(true);
    try {
      await onAdd({ texto: txt.trim(), tipo: tipo || undefined, proximo_followup: fuOn ? fu.data : undefined, followup_hora: fuOn ? fu.hora : undefined });
      setTxt(''); setTipo(''); setFuOn(false); setFu({ data: '', hora: '' });
    } catch (err) {
      toast.error(`Contato NÃO registrado — ${err.response?.data?.detail || 'erro de conexão'}`);
    } finally { setSending(false); }
  };
  const vistos = new Set();
  return (
    <div className="space-y-1.5 text-xs" data-testid={`contatos-${o.numero}`}>
      <div className="font-semibold uppercase text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />Contatos com o cliente</div>
      {(o.contatos || []).map(c => {
        const chave = `${c.autor}|${c.texto}`;
        const possivelDup = vistos.has(chave);
        vistos.add(chave);
        return (
        <div key={c.id} className="rounded border bg-background/60 px-2 py-1 flex items-start gap-2" data-testid={`contato-item-${c.id}`}>
          <div className="flex-1">
            <span className="text-muted-foreground">{new Date(c.data).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} • <b>{c.autor}</b>{c.tipo ? ` • ${TIPO_LABEL[c.tipo] || c.tipo}` : ''}:</span> {c.texto}
            {possivelDup && <span className="ml-1.5 text-[10px] rounded bg-amber-100 text-amber-700 px-1 py-0.5 font-semibold" data-testid={`contato-dup-${c.id}`}>possível duplicado</span>}
          </div>
          {admin && <button onClick={() => { if (window.confirm('Excluir este registro de contato?')) onDelContato(c.id); }} data-testid={`contato-del-${c.id}`} title="Excluir contato (somente admin)"><Trash2 className="w-3 h-3 text-stone-400 hover:text-red-500 mt-0.5" /></button>}
        </div>
        );
      })}
      {(o.contatos || []).length === 0 && <p className="text-muted-foreground italic">Nenhum contato registrado ainda.</p>}
      {o.proximo_followup && (
        <div className={`flex items-center gap-2 rounded border px-2 py-1 ${o.proximo_followup < hj ? 'border-red-300 bg-red-50 dark:bg-red-900/20 text-red-700' : 'bg-background/60'}`} data-testid={`followup-atual-${o.numero}`}>
          <AlertCircle className="w-3 h-3" />
          <span>Próximo follow-up: <b>{fmtData(o.proximo_followup)}</b>{o.followup_hora ? ` às ${o.followup_hora}` : ''}{o.followup_resp ? ` • ${o.followup_resp}` : ''}{o.proximo_followup < hj ? ' (VENCIDO)' : ''}</span>
          <button onClick={() => onFollowup({})} className="ml-auto rounded border px-1.5 py-0.5 text-[10px] hover:bg-muted" data-testid={`followup-concluir-${o.numero}`}>Concluir</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <select value={tipo} onChange={e => setTipo(e.target.value)} className="h-8 rounded border px-1.5 bg-background" data-testid={`contato-tipo-${o.numero}`} disabled={sending}>
          <option value="">Tipo...</option>
          {TIPOS_CONTATO.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input value={txt} onChange={e => setTxt(e.target.value)} placeholder="Ligação, WhatsApp, e-mail... o que foi conversado"
          onKeyDown={e => { if (e.key === 'Enter') enviar(); }} disabled={sending}
          className="h-8 flex-1 min-w-[200px] rounded border px-2 bg-background" data-testid={`contato-input-${o.numero}`} />
        <label className="flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={fuOn} onChange={e => setFuOn(e.target.checked)} data-testid={`contato-fu-check-${o.numero}`} className="accent-sky-600" />Agendar follow-up
        </label>
        {fuOn && <>
          <input type="date" value={fu.data} onChange={e => setFu(p => ({ ...p, data: e.target.value }))} className="h-8 rounded border px-1.5 bg-background" data-testid={`contato-fu-data-${o.numero}`} />
          <input type="time" value={fu.hora} onChange={e => setFu(p => ({ ...p, hora: e.target.value }))} className="h-8 rounded border px-1.5 bg-background" data-testid={`contato-fu-hora-${o.numero}`} />
        </>}
        <Button size="sm" disabled={!txt.trim() || sending} onClick={enviar} data-testid={`contato-add-${o.numero}`}>{sending ? 'Salvando...' : 'Registrar'}</Button>
      </div>
    </div>
  );
}

function OrcDetalhe({ o, onSave }) {
  const [f, setF] = useState({
    nf_numero: o.nf_numero || '', nf_data: o.nf_data || '', retencao: o.retencao || '', cortesia: !!o.cortesia,
    recebimentos: o.recebimentos?.length ? o.recebimentos.map(r => ({ ...r }))
      : (o.valor_recebido ? [{ valor: o.valor_recebido, data: '', onde: o.recebido_info || '' }] : []),
  });
  const setReceb = (i, k, v) => setF(p => ({ ...p, recebimentos: p.recebimentos.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const totalReceb = f.recebimentos.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
  const pend = Math.max(0, (o.total || 0) - totalReceb - (parseFloat(String(f.retencao).replace(',', '.')) || 0));
  return (
    <div className="flex flex-wrap gap-6 text-xs" data-testid={`detalhe-${o.numero}`}>
      <div className="space-y-1.5">
        <div className="font-semibold uppercase text-muted-foreground">Nota fiscal</div>
        <div className="flex gap-2">
          <input placeholder="Nº da NF" value={f.nf_numero} onChange={e => setF({ ...f, nf_numero: e.target.value })} className="h-8 w-28 rounded border px-2 bg-background" data-testid={`nf-numero-${o.numero}`} />
          <input type="date" value={f.nf_data} onChange={e => setF({ ...f, nf_data: e.target.value })} className="h-8 rounded border px-2 bg-background" data-testid={`nf-data-${o.numero}`} title="Data de emissão" />
        </div>
        <div className="font-semibold uppercase text-muted-foreground pt-2">Retenção (R$)</div>
        <input type="number" step="0.01" placeholder="0,00" value={f.retencao} onChange={e => setF({ ...f, retencao: e.target.value })} className="h-8 w-28 rounded border px-2 bg-background" data-testid={`retencao-${o.numero}`} />
        <label className="flex items-center gap-1.5 pt-2 cursor-pointer font-semibold text-pink-700 dark:text-pink-400">
          <input type="checkbox" checked={f.cortesia} onChange={e => setF({ ...f, cortesia: e.target.checked })} className="w-4 h-4 accent-pink-600" data-testid={`cortesia-${o.numero}`} />
          CORTESIA (não cobra e não soma nos totais)
        </label>
      </div>
      <div className="space-y-1.5 flex-1 min-w-[320px]">
        <div className="font-semibold uppercase text-muted-foreground">Recebimentos (parciais até quitar)</div>
        {f.recebimentos.map((r, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input type="number" step="0.01" placeholder="Valor" value={r.valor} onChange={e => setReceb(i, 'valor', e.target.value)} className="h-8 w-28 rounded border px-2 bg-background" data-testid={`receb-valor-${o.numero}-${i}`} />
            <input type="date" value={r.data} onChange={e => setReceb(i, 'data', e.target.value)} className="h-8 rounded border px-2 bg-background" data-testid={`receb-data-${o.numero}-${i}`} />
            <input placeholder="Onde/forma (PIX, Bradesco...)" value={r.onde} onChange={e => setReceb(i, 'onde', e.target.value)} className="h-8 flex-1 rounded border px-2 bg-background" data-testid={`receb-onde-${o.numero}-${i}`} />
            <button onClick={() => setF(p => ({ ...p, recebimentos: p.recebimentos.filter((_, j) => j !== i) }))} data-testid={`receb-del-${o.numero}-${i}`}><Trash2 className="w-3.5 h-3.5 text-stone-400 hover:text-red-500" /></button>
          </div>
        ))}
        <button onClick={() => setF(p => ({ ...p, recebimentos: [...p.recebimentos, { valor: '', data: '', onde: '' }] }))} className="text-teal-700 dark:text-teal-400 font-medium" data-testid={`receb-add-${o.numero}`}>+ Adicionar recebimento</button>
        <div className="pt-1 flex gap-4">
          <span>Recebido: <b className="text-teal-700 dark:text-teal-400">{fmtBRL(totalReceb)}</b></span>
          <span>Pendente: <b className={pend > 0 ? 'text-red-600' : 'text-emerald-600'}>{fmtBRL(pend)}</b></span>
        </div>
      </div>
      <div className="flex items-end">
        <Button size="sm" data-testid={`salvar-detalhe-${o.numero}`} onClick={() => onSave({
          nf_numero: f.nf_numero, nf_data: f.nf_data, cortesia: f.cortesia,
          retencao: parseFloat(String(f.retencao).replace(',', '.')) || 0,
          recebimentos: f.recebimentos.filter(r => parseFloat(r.valor)).map(r => ({ valor: parseFloat(String(r.valor).replace(',', '.')) || 0, data: r.data || '', onde: r.onde || '' })),
        })}>Salvar</Button>
      </div>
    </div>
  );
}

export default function OrcamentosPage() {
  const [aba, setAba] = useState('orcamentos');
  const [loading, setLoading] = useState(true);
  const [itens, setItens] = useState([]);
  const [resumo, setResumo] = useState({});
  const [leads, setLeads] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [filtroSit, setFiltroSit] = useState('');
  const [filtroPag, setFiltroPag] = useState('');
  const [busca, setBusca] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importTexto, setImportTexto] = useState('');
  const [novoOrc, setNovoOrc] = useState(null);
  const [novoLead, setNovoLead] = useState(null);
  const [formEv, setFormEv] = useState(null);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [sugestoes, setSugestoes] = useState([]);
  const [diaSel, setDiaSel] = useState(null);
  const [expPipe, setExpPipe] = useState(null);
  const [valoresOcultos, setValoresOcultos] = useState(false);
  const [podeAlterarSit, setPodeAlterarSit] = useState(false);
  const [colW, setColW] = useState(() => { try { return JSON.parse(localStorage.getItem('orc_col_w') || '{}'); } catch { return {}; } });
  useEffect(() => { localStorage.setItem('orc_col_w', JSON.stringify(colW)); }, [colW]);
  const [expandido, setExpandido] = useState(null);
  const [novaSolic, setNovaSolic] = useState(false);
  const [solicTexto, setSolicTexto] = useState('');
  const [calRef, setCalRef] = useState(() => { const d = new Date(); return { ano: d.getFullYear(), mes: d.getMonth() + 1 }; });

  const carregar = useCallback(async () => {
    try {
      const [o, l, e, s, sg] = await Promise.all([api.get('/orcamentos'), api.get('/crm/leads'), api.get('/eventos-cidade'), api.get('/orcamentos/solicitacoes'), api.get('/eventos-sugestoes')]);
      setItens(o.data.itens); setResumo(o.data.resumo); setLeads(l.data); setEventos(e.data); setSolicitacoes(s.data); setSugestoes(sg.data);
      setValoresOcultos(!!o.data.valores_ocultos);
      setPodeAlterarSit(!!o.data.pode_alterar_situacao);
    } catch (err) {
      toast.error(err.response?.status === 403 ? 'Acesso restrito a administradores' : 'Erro ao carregar');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const patchOrc = async (id, campos) => {
    try { await api.put(`/orcamentos/${id}`, campos); carregar(); }
    catch (err) { toast.error(`Alteração NÃO salva — ${err.response?.data?.detail || 'erro de conexão'}`); }
  };
  const patchLead = async (id, campos) => { await api.put(`/crm/leads/${id}`, campos); carregar(); };

  const [buscandoEv, setBuscandoEv] = useState(false);
  const buscarEventosWeb = useCallback(async (mes) => {
    setBuscandoEv(true);
    try {
      const r = await api.post('/eventos-cidade/buscar', { mes });
      toast.success(`Busca concluída: ${r.data.novos} sugestão(ões) nova(s), ${r.data.ja_existiam} já existente(s)/com OS`);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na busca de eventos na web');
    } finally { setBuscandoEv(false); }
  }, [carregar]);

  useEffect(() => {
    if (aba !== 'calendario') return;
    api.get('/eventos-cidade-meta').then(r => {
      const ult = r.data?.ultima_busca;
      if (!ult || Date.now() - new Date(ult).getTime() > 6 * 3600 * 1000) buscarEventosWeb();
    }).catch(() => {});
  }, [aba, buscarEventosWeb]);

  const [filtroMes, setFiltroMes] = useState(() => new Date().toISOString().slice(0, 7));
  const mesesDisponiveis = [...new Set([...itens.map(o => o.data?.slice(0, 7)).filter(Boolean), new Date().toISOString().slice(0, 7)])].sort();

  const filtradosPeriodo = itens.filter(o =>
    (!filtroMes || o.data?.startsWith(filtroMes)) &&
    (!filtroPag || o.pagamento === filtroPag) &&
    (!busca || o.cliente.toLowerCase().includes(busca.toLowerCase()) || o.numero.includes(busca))
  );
  const filtrados = filtradosPeriodo.filter(o => !filtroSit || o.situacao === filtroSit);

  const totaisPorStatus = {};
  filtradosPeriodo.forEach(o => {
    const t = (totaisPorStatus[o.situacao] = totaisPorStatus[o.situacao] || { qtd: 0, valor: 0 });
    t.qtd += 1; t.valor += valorEfetivo(o);
  });
  const eventosProspectar = eventos.filter(e => !e.os_vinculada && !['ja_orcado', 'nao_prospectar'].includes(normStatus(e.status_abordagem)));
  const aOrcarQtd = solicitacoes.filter(s => s.status === 'a_orcar').length;
  const solicPendentes = [...solicitacoes].filter(s => !['convertido', 'descartado'].includes(s.status)).sort((a, b) => (a.status === 'a_orcar' ? 0 : 1) - (b.status === 'a_orcar' ? 0 : 1));

  const hoje = new Date().toISOString().slice(0, 10);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;

  const prospAbertas = eventos.filter(e => !e.os_vinculada && ['prospeccao', 'contato_feito', 'aguardando_retorno'].includes(normStatus(e.status_abordagem)));
  const confPeriodo = filtradosPeriodo.filter(o => o.situacao === 'confirmado');
  const cards = valoresOcultos ? [
    { t: 'Confirmados', v: confPeriodo.length, icon: Target, cor: 'text-emerald-600', qt: true },
    { t: 'A confirmar', v: filtradosPeriodo.filter(o => o.situacao === 'a_confirmar').length, icon: Clock, cor: 'text-amber-600', qt: true },
    { t: 'Reservados', v: filtradosPeriodo.filter(o => o.situacao === 'reservado').length, icon: CalendarDays, cor: 'text-indigo-600', qt: true },
    { t: 'Prospecções abertas', v: prospAbertas.length, icon: Target, cor: 'text-sky-600', qt: true },
    { t: 'Follow-ups vencidos', v: prospAbertas.filter(e => e.proximo_followup && e.proximo_followup < hoje).length + itens.filter(o => o.proximo_followup && o.proximo_followup < hoje && !['cancelado', 'nao_executada'].includes(o.situacao)).length, icon: AlertCircle, cor: 'text-red-600', qt: true },
  ] : [
    { t: 'Confirmado', v: confPeriodo.reduce((s, o) => s + valorEfetivo(o), 0), icon: Target, cor: 'text-emerald-600' },
    { t: 'Recebido', v: filtradosPeriodo.reduce((s, o) => s + recebTotal(o), 0), icon: DollarSign, cor: 'text-teal-600' },
    { t: 'A cobrar', v: confPeriodo.reduce((s, o) => s + pendenteDe(o), 0), icon: AlertCircle, cor: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900' },
    { t: 'Falta faturar', v: confPeriodo.filter(o => !o.nota_emitida && !o.nf_numero && !o.cortesia).reduce((s, o) => s + valorEfetivo(o), 0), icon: Clock, cor: 'text-amber-600' },
    { t: 'Pipeline (não confirmado)', v: filtradosPeriodo.filter(o => ['a_confirmar', 'opcao', 'reservado'].includes(o.situacao)).reduce((s, o) => s + valorEfetivo(o), 0), icon: CalendarDays, cor: 'text-sky-600' },
  ];
  const mesesGraf = Array.from({ length: 4 }, (_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + i); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; });
  const dataGraf = mesesGraf.map(m => {
    const row = { mes: m.split('-').reverse().join('/') };
    GRAF_SITS.forEach(s => {
      const lst = itens.filter(o => o.data?.startsWith(m) && o.situacao === s);
      row[s] = valoresOcultos ? lst.length : Math.round(lst.reduce((t, o) => t + valorEfetivo(o), 0) * 100) / 100;
    });
    return row;
  });
  const NCOLS = valoresOcultos ? 7 : 13;

  // Calendário
  const diasNoMes = new Date(calRef.ano, calRef.mes, 0).getDate();
  const mesStr = `${calRef.ano}-${String(calRef.mes).padStart(2, '0')}`;
  const porDia = {};
  itens.forEach(o => { if (o.data?.startsWith(mesStr) && !['cancelado', 'nao_executada'].includes(o.situacao)) (porDia[o.data] = porDia[o.data] || { orc: [], ev: [] }).orc.push(o); });
  eventos.forEach(e => { if (e.data?.startsWith(mesStr)) (porDia[e.data] = porDia[e.data] || { orc: [], ev: [] }).ev.push(e); });

  return (
    <div className="space-y-5" data-testid="orcamentos-page">
      {/* Cards resumo do período selecionado */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(c => (
          <Card key={c.t} className={c.bg || ''}><CardContent className="p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><c.icon className={`w-4 h-4 ${c.cor}`} />{c.t}</div>
            <div className={`text-lg font-bold mt-1 ${c.cor}`} data-testid={`card-${c.t.toLowerCase().replace(/[^a-z]/g, '-')}`}>{c.qt ? c.v : fmtBRL(c.v)}</div>
            <div className="text-[10px] text-muted-foreground">{c.t.startsWith('Prospec') || c.t.startsWith('Follow') ? 'em aberto' : (filtroMes ? filtroMes.split('-').reverse().join('/') : 'todos os meses')}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Abas */}
      <div className="flex gap-2 border-b pb-2">
        {[['orcamentos', 'Orçamentos'], ['crm', 'Pipeline Comercial'], ['calendario', 'Calendário & Prospecção']].map(([k, l]) => (
          <Button key={k} data-testid={`aba-${k}`} variant={aba === k ? 'default' : 'outline'} size="sm" onClick={() => setAba(k)}>{l}</Button>
        ))}
      </div>

      {aba === 'orcamentos' && (
        <div className="space-y-3">
          {/* Gráfico prospectivo: mês atual + 3 próximos (por data do evento) + totais do período */}
          <div className="flex flex-wrap items-center gap-4">
            <BarChart width={440} height={160} data={dataGraf} data-testid="grafico-4meses">
              <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 9 }} width={valoresOcultos ? 24 : 46} tickFormatter={v => valoresOcultos ? v : (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
              <RTooltip formatter={(v, n) => [valoresOcultos ? `${v} OS` : fmtBRL(v), n]} />
              {GRAF_SITS.map(s => <Bar key={s} dataKey={s} stackId="a" fill={STATUS_HEX[s]} name={SITUACOES[s]?.label || s} />)}
            </BarChart>
            <div className="flex flex-wrap gap-2 flex-1" data-testid="totais-status">
            {Object.entries(SITUACOES).map(([k, v]) => totaisPorStatus[k] ? (
              <button key={k} onClick={() => setFiltroSit(filtroSit === k ? '' : k)}
                className={`text-xs rounded-full px-2.5 py-1 ${v.cor} ${filtroSit === k ? 'ring-2 ring-offset-1 ring-stone-500' : ''}`} data-testid={`total-${k}`}>
                {v.label}: <b>{totaisPorStatus[k].qtd}</b>{!valoresOcultos && <> • {fmtBRL(totaisPorStatus[k].valor)}</>}
              </button>
            ) : null)}
            </div>
          </div>

          {/* Eventos a prospectar */}
          {eventosProspectar.length > 0 && (
            <div className="flex gap-2 items-center overflow-x-auto rounded-lg border border-purple-300 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 px-3 py-2" data-testid="faixa-prospeccao">
              <span className="text-xs font-semibold text-purple-700 dark:text-purple-300 whitespace-nowrap">★ A prospectar:</span>
              {eventosProspectar.map(e => (
                <span key={e.id} className="text-xs whitespace-nowrap rounded bg-white dark:bg-purple-950 border border-purple-200 dark:border-purple-800 px-2 py-0.5" title={`${e.local || ''} ${e.organizador || ''}`}>
                  {fmtData(e.data)} — {e.nome}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 items-center">
            <select value={filtroMes} onChange={e => setFiltroMes(e.target.value)} className="h-8 text-sm rounded border px-2" data-testid="filtro-mes">
              <option value="">Todos os meses</option>
              {mesesDisponiveis.map(m => <option key={m} value={m}>{m.split('-').reverse().join('/')}{m === new Date().toISOString().slice(0, 7) ? ' (atual)' : ''}</option>)}
            </select>
            <Input placeholder="Buscar cliente ou nº..." value={busca} onChange={e => setBusca(e.target.value)} className="h-8 w-56" data-testid="busca-orcamento" />
            <select value={filtroSit} onChange={e => setFiltroSit(e.target.value)} className="h-8 text-sm rounded border px-2" data-testid="filtro-situacao">
              <option value="">Todas situações</option>
              {Object.entries(SITUACOES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            {!valoresOcultos && <select value={filtroPag} onChange={e => setFiltroPag(e.target.value)} className="h-8 text-sm rounded border px-2" data-testid="filtro-pagamento">
              <option value="">Todos pagamentos</option>
              {Object.entries(PAGAMENTOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>}
            <span className="text-xs text-muted-foreground">{filtrados.length} de {itens.length}</span>
            <div className="flex-1" />
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-400" onClick={() => setNovaSolic(!novaSolic)} data-testid="btn-nova-solicitacao"><FileText className="w-4 h-4 mr-1" />Colar solicitação{aOrcarQtd ? ` (${aOrcarQtd})` : ''}</Button>
            {!valoresOcultos && <Button size="sm" variant="outline" onClick={() => setShowImport(!showImport)} data-testid="btn-importar"><Upload className="w-4 h-4 mr-1" />Importar</Button>}
            {!valoresOcultos && <Button size="sm" onClick={() => setNovoOrc({ numero: '', data: hoje, cliente: '', total: 0, situacao: 'a_confirmar', pagamento: 'nao_faturado' })} data-testid="btn-novo-orcamento"><Plus className="w-4 h-4 mr-1" />Novo</Button>}
          </div>

          {novaSolic && (
            <Card><CardContent className="p-3 space-y-2">
              <p className="text-xs text-muted-foreground">Cole o texto da solicitação (site, e-mail, WhatsApp) — nome, contato, evento, data e local são extraídos automaticamente. Ela entra na relação como <b>OS A FAZER</b> até você mudar o status.</p>
              <Textarea rows={8} placeholder={'Nome: ...\nEmail: ...\nTelefone: ...\nNome do evento/projeto: ...\nData prevista: ...\nLocal previsto de execução: ...\n(ou qualquer texto livre)'} value={solicTexto} onChange={e => setSolicTexto(e.target.value)} data-testid="solic-textarea" />
              <Button size="sm" disabled={!solicTexto.trim()} data-testid="btn-salvar-solicitacao" onClick={async () => {
                const r = await api.post('/orcamentos/solicitacoes', { texto: solicTexto });
                setSolicTexto(''); setNovaSolic(false); carregar();
                toast.success(r.data.duplicado ? 'Solicitação já existia — dados atualizados, sem duplicar' : 'Solicitação registrada');
              }}>Salvar solicitação</Button>
            </CardContent></Card>
          )}

          {showImport && (
            <Card><CardContent className="p-3 space-y-2">
              <p className="text-xs text-muted-foreground">Cole o relatório do sistema antigo (número/data/cliente/total/situação). Importa o histórico completo; status convertidos automaticamente; o que você já ajustou aqui (pagamento, NF, contrato, recebido, obs) é preservado.</p>
              <Textarea rows={6} value={importTexto} onChange={e => setImportTexto(e.target.value)} data-testid="import-textarea" />
              <Button size="sm" disabled={!importTexto.trim()} data-testid="btn-confirmar-import" onClick={async () => {
                const r = await api.post('/orcamentos/importar', { texto: importTexto, data_corte: '2026-01-01' });
                toast.success(`${r.data.novos} novos, ${r.data.atualizados} atualizados`);
                setImportTexto(''); setShowImport(false); carregar();
              }}>Importar agora</Button>
            </CardContent></Card>
          )}

          {novoOrc && (
            <Card><CardContent className="p-3 flex flex-wrap gap-2 items-end">
              <Input placeholder="Número" value={novoOrc.numero} onChange={e => setNovoOrc({ ...novoOrc, numero: e.target.value })} className="h-8 w-28" data-testid="novo-numero" />
              <Input type="date" value={novoOrc.data} onChange={e => setNovoOrc({ ...novoOrc, data: e.target.value })} className="h-8 w-40" data-testid="novo-data" />
              <Input placeholder="Cliente" value={novoOrc.cliente} onChange={e => setNovoOrc({ ...novoOrc, cliente: e.target.value })} className="h-8 flex-1 min-w-[200px]" data-testid="novo-cliente" />
              <Input type="number" placeholder="Total" value={novoOrc.total} onChange={e => setNovoOrc({ ...novoOrc, total: parseFloat(e.target.value) || 0 })} className="h-8 w-32" data-testid="novo-total" />
              <Button size="sm" data-testid="salvar-orcamento" onClick={async () => {
                if (!novoOrc.numero || !novoOrc.cliente) { toast.error('Número e cliente obrigatórios'); return; }
                await api.post('/orcamentos', novoOrc); setNovoOrc(null); carregar(); toast.success('Orçamento criado');
              }}>Salvar</Button>
              <Button size="sm" variant="ghost" onClick={() => setNovoOrc(null)}>Cancelar</Button>
            </CardContent></Card>
          )}

          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm orc-fixed">
              <thead className="bg-muted/50 text-xs uppercase">
                <tr><ThR k="num" colW={colW} setColW={setColW} className="p-2 text-left">Nº</ThR><ThR k="data" colW={colW} setColW={setColW} className="p-2 text-left">Data</ThR><ThR k="cliente" colW={colW} setColW={setColW} className="p-2 text-left">Cliente</ThR>{!valoresOcultos && <ThR k="total" colW={colW} setColW={setColW} className="p-2 text-right">Total</ThR>}<ThR k="situacao" colW={colW} setColW={setColW} className="p-2">Situação</ThR>{!valoresOcultos && <><ThR k="obs" colW={colW} setColW={setColW} className="p-2 text-left">Obs adm</ThR><ThR k="pagamento" colW={colW} setColW={setColW} className="p-2">Pagamento</ThR><ThR k="nf" colW={colW} setColW={setColW} className="p-2">NF</ThR></>}<ThR k="contr" colW={colW} setColW={setColW} className="p-2">Contr.</ThR>{!valoresOcultos && <><ThR k="recebido" colW={colW} setColW={setColW} className="p-2 text-right">Recebido</ThR><ThR k="pendente" colW={colW} setColW={setColW} className="p-2 text-right">Pendente</ThR></>}<ThR k="contatos" colW={colW} setColW={setColW} className="p-2">Contatos</ThR><th className="p-2" style={{ width: LARGURAS_PADRAO.acoes }}></th></tr>
              </thead>
              <tbody>
                {solicPendentes.map(s => (
                  <React.Fragment key={s.id}>
                  <tr className="border-t border-l-4 border-l-amber-400 bg-amber-50 dark:bg-amber-900/20" data-testid={`solic-row-${s.id}`}>
                    <td className="p-2 text-[10px] font-bold text-amber-700 dark:text-amber-400 whitespace-nowrap">{s.status === 'a_orcar' ? 'OS A FAZER' : 'ORÇ. ENVIADO'}</td>
                    <td className="p-2 whitespace-nowrap font-semibold text-amber-700 dark:text-amber-400">{fmtData(hoje)}</td>
                    <td className="p-2 max-w-[280px] truncate" title={`${s.nome || ''} ${s.empresa || ''} ${s.evento || ''}`}>
                      {s.nome || 'Sem nome'}{s.empresa ? ` — ${s.empresa}` : ''}{s.evento ? <span className="text-muted-foreground"> • {s.evento}</span> : ''}
                    </td>
                    {!valoresOcultos && <td className="p-2 text-right text-muted-foreground">—</td>}
                    <td className="p-2">
                      <select value={s.status} onChange={async e => { await api.put(`/orcamentos/solicitacoes/${s.id}`, { status: e.target.value }); carregar(); }}
                        data-testid={`solic-status-${s.id}`} className={`text-xs rounded border px-1.5 py-1 ${SOLIC_STATUS[s.status]?.cor || ''}`}>
                        {Object.entries(SOLIC_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </td>
                    <td className="p-2 text-xs text-muted-foreground" colSpan={valoresOcultos ? 2 : 7}>
                      {s.data_prevista && <span><b>Prevista:</b> {s.data_prevista} </span>}{s.horario && <span>• {s.horario} </span>}
                      {s.telefone && <span>• {s.telefone} </span>}{s.email && <span>• {s.email}</span>}
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      <button onClick={() => setExpandido(expandido === s.id ? null : s.id)} data-testid={`solic-expand-${s.id}`} title="Ver texto completo" className="mr-1.5 align-middle">
                        {expandido === s.id ? <ChevronUp className="w-4 h-4 text-amber-600" /> : <ChevronDown className="w-4 h-4 text-amber-600" />}
                      </button>
                      {!valoresOcultos && <button onClick={async () => { if (window.confirm('Excluir solicitação?')) { await api.delete(`/orcamentos/solicitacoes/${s.id}`); carregar(); } }} data-testid={`solic-del-${s.id}`} className="align-middle"><Trash2 className="w-3.5 h-3.5 text-stone-400 hover:text-red-500" /></button>}
                    </td>
                  </tr>
                  {expandido === s.id && (
                    <tr className="border-t bg-amber-50/60 dark:bg-amber-900/10">
                      <td colSpan={NCOLS} className="p-3 text-xs space-y-1.5" data-testid={`solic-detalhe-${s.id}`}>
                        {s.local && <div><b>Local:</b> {s.local}</div>}
                        {s.servico && <div><b>Serviço:</b> {s.servico}</div>}
                        <pre className="whitespace-pre-wrap text-[11px] bg-background/60 rounded p-2 max-h-56 overflow-y-auto font-sans border">{s.texto}</pre>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
                {filtrados.map(o => (
                  <React.Fragment key={o.id}>
                  <tr className="border-t hover:bg-muted/30" data-testid={`orc-row-${o.numero}`}>
                    <td className="p-2 font-mono text-xs">{o.numero}</td>
                    <td className="p-2 whitespace-nowrap">{fmtData(o.data)}</td>
                    <td className="p-2 max-w-[280px] truncate" title={o.cliente}>{o.cliente}</td>
                    {!valoresOcultos && <td className="p-2 text-right font-medium whitespace-nowrap">
                      {o.cortesia ? (
                        <span title={`Cortesia — valor de ${fmtBRL(o.total)} não cobrado e fora dos totais`}>
                          <span className="text-[10px] rounded bg-pink-100 text-pink-700 px-1.5 py-0.5 font-bold align-middle">CORTESIA</span>
                          <span className="block text-[10px] text-muted-foreground line-through">{fmtBRL(o.total)}</span>
                        </span>
                      ) : fmtBRL(o.total)}
                    </td>}
                    <td className="p-2">
                      {valoresOcultos && !podeAlterarSit ? (
                        <span className={`text-xs rounded px-1.5 py-1 ${SITUACOES[o.situacao]?.cor || ''}`} data-testid={`sit-${o.numero}`}>{SITUACOES[o.situacao]?.label || o.situacao}</span>
                      ) : (
                      <select value={o.situacao} onChange={e => patchOrc(o.id, { situacao: e.target.value })} data-testid={`sit-${o.numero}`}
                        className={`text-xs rounded px-1.5 py-1 border-0 ${SITUACOES[o.situacao]?.cor || ''}`}>
                        {Object.entries(SITUACOES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                      )}
                    </td>
                    {!valoresOcultos && <>
                    <td className="p-2">
                      <ObsAdmInput key={`obs-${o.id}`} o={o} />
                    </td>
                    {INATIVOS.includes(o.situacao) ? (
                      <td colSpan={5} className="p-2 text-center text-xs text-muted-foreground/60 italic" data-testid={`na-${o.numero}`}>— não se aplica —</td>
                    ) : (<>
                    <td className="p-2">
                      <select value={o.pagamento} onChange={e => patchOrc(o.id, { pagamento: e.target.value })} data-testid={`pag-${o.numero}`}
                        className={`text-xs rounded px-1.5 py-1 border ${o.pagamento === 'a_cobrar' || o.pagamento === 'pago_50_cobrar' ? 'bg-red-100 text-red-700 font-semibold' : o.pagamento === 'pago_100' ? 'bg-emerald-100 text-emerald-700' : ''}`}>
                        {Object.entries(PAGAMENTOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                    <td className="p-2 text-center whitespace-nowrap">
                      {o.nf_numero ? (
                        <button onClick={() => setExpandido(expandido === o.id ? null : o.id)} data-testid={`nf-${o.numero}`}
                          className="text-xs rounded bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-semibold" title={`NF ${o.nf_numero}${o.nf_data ? ' emitida em ' + fmtData(o.nf_data) : ''}`}>
                          {o.nf_numero}{o.nf_data ? ` • ${fmtData(o.nf_data)}` : ''}
                        </button>
                      ) : (
                        <input type="checkbox" checked={!!o.nota_emitida} onChange={e => patchOrc(o.id, { nota_emitida: e.target.checked })} data-testid={`nf-${o.numero}`} className="w-4 h-4 accent-emerald-600" title="NF emitida (expanda a linha para nº e data)" />
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <input type="checkbox" checked={!!o.contrato} onChange={e => patchOrc(o.id, { contrato: e.target.checked })} data-testid={`contrato-${o.numero}`} className="w-4 h-4 accent-sky-600" title="Contrato assinado" />
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <button onClick={() => setExpandido(expandido === o.id ? null : o.id)} data-testid={`recebido-${o.numero}`}
                        className={recebTotal(o) > 0 ? 'text-teal-700 dark:text-teal-400 font-medium underline decoration-dotted' : 'text-muted-foreground'}>
                        {fmtBRL(recebTotal(o))}{o.recebimentos?.length > 1 ? ` (${o.recebimentos.length}x)` : ''}
                      </button>
                    </td>
                    <td className={`p-2 text-right font-semibold whitespace-nowrap ${pendenteDe(o) > 0 && o.situacao === 'confirmado' ? 'text-red-600' : 'text-muted-foreground'}`} data-testid={`pendente-${o.numero}`}>
                      {fmtBRL(pendenteDe(o))}
                      {o.retencao > 0 && <div className="text-[10px] text-amber-600 font-normal">ret. {fmtBRL(o.retencao)}</div>}
                    </td>
                    </>)}
                    </>}
                    {valoresOcultos && <td className="p-2 text-center"><input type="checkbox" checked={!!o.contrato} disabled className="w-4 h-4 accent-sky-600" title="Contrato assinado (somente leitura)" data-testid={`contrato-${o.numero}`} /></td>}
                    <td className="p-2 text-center whitespace-nowrap">
                      <button onClick={() => setExpandido(expandido === o.id ? null : o.id)} data-testid={`contatos-btn-${o.numero}`} title="Registrar/ver contatos com o cliente"
                        className={`inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 ${(o.contatos?.length || 0) > 0 ? 'bg-sky-100 text-sky-700 font-semibold' : 'text-muted-foreground border'}`}>
                        <Phone className="w-3 h-3" />{o.contatos?.length || 0}
                      </button>
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      <button onClick={() => setExpandido(expandido === o.id ? null : o.id)} data-testid={`expand-${o.numero}`} title={valoresOcultos ? 'Contatos com o cliente' : 'NF, recebimentos, retenção, contatos'} className="mr-1.5 align-middle">
                        {expandido === o.id ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
                      </button>
                      {!valoresOcultos && <button onClick={async () => { if (window.confirm('Excluir?')) { await api.delete(`/orcamentos/${o.id}`); carregar(); } }} data-testid={`del-${o.numero}`} className="align-middle"><Trash2 className="w-3.5 h-3.5 text-stone-400 hover:text-red-500" /></button>}
                    </td>
                  </tr>
                  {expandido === o.id && (
                    <tr className="border-t bg-muted/20">
                      <td colSpan={NCOLS} className="p-3 space-y-4">
                        {!valoresOcultos && <OrcDetalhe o={o} onSave={async (campos) => { await patchOrc(o.id, campos); toast.success('Dados salvos'); }} />}
                        <ContatosOrc o={o} admin={!valoresOcultos}
                          onAdd={async (payload) => { await api.post(`/orcamentos/${o.id}/contatos`, payload); await carregar(); toast.success('Contato registrado'); }}
                          onFollowup={async (campos) => { try { await api.put(`/orcamentos/${o.id}/followup`, campos); carregar(); toast.success('Follow-up concluído'); } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao atualizar follow-up'); } }}
                          onDelContato={async (cid) => { try { await api.delete(`/orcamentos/${o.id}/contatos/${cid}`); carregar(); } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); } }} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {aba === 'crm' && (() => {
        const prospPipe = eventos.filter(e => !e.os_vinculada && ['prospeccao', 'contato_feito', 'aguardando_retorno'].includes(normStatus(e.status_abordagem)) && (!filtroMes || !e.data || e.data.startsWith(filtroMes)));
        const itensMes = itens.filter(o => !filtroMes || o.data?.startsWith(filtroMes));
        const etapasOs = [['a_confirmar', 'À confirmar'], ['reservado', 'Reservado'], ['confirmado', 'Confirmado'], ['adiado', 'Adiado']];
        return (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <select value={filtroMes} onChange={e => setFiltroMes(e.target.value)} className="h-8 text-sm rounded border px-2" data-testid="pipe-filtro-mes">
              <option value="">Todos os meses</option>
              {mesesDisponiveis.map(m => <option key={m} value={m}>{m.split('-').reverse().join('/')}{m === new Date().toISOString().slice(0, 7) ? ' (atual)' : ''}</option>)}
            </select>
            <p className="text-xs text-muted-foreground">Prospecções sem OS nas 3 primeiras colunas • com OS vinculada/criada, o card segue automaticamente a situação da OS (pela data do evento) • follow-up vencido em vermelho</p>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2" data-testid="pipeline-board">
            {ETAPAS_PROSP.map(([k, label]) => {
              const col = prospPipe.filter(e => normStatus(e.status_abordagem) === k);
              return (
                <div key={k} className="rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50/40 dark:bg-sky-900/10 p-2 min-w-[240px] w-[240px] shrink-0" data-testid={`col-${k}`}>
                  <div className="text-xs font-semibold uppercase mb-2 flex justify-between text-sky-800 dark:text-sky-300"><span>{label}</span><span>{col.length}</span></div>
                  <div className="space-y-2">
                    {col.map(e => <ProspCard key={e.id} e={e} admin={!valoresOcultos} onEdit={() => { setFormEv(e); setAba('calendario'); }} onReload={carregar} />)}
                    {col.length === 0 && <p className="text-[11px] text-muted-foreground italic">vazio</p>}
                  </div>
                </div>
              );
            })}
            {etapasOs.map(([k, label]) => {
              const col = itensMes.filter(o => o.situacao === k);
              return (
                <div key={k} className="rounded-lg border bg-muted/20 p-2 min-w-[240px] w-[240px] shrink-0" data-testid={`col-${k}`}>
                  <div className="text-xs font-semibold uppercase mb-2 flex justify-between"><span className={`rounded px-1.5 ${SITUACOES[k]?.cor || ''}`}>{label}</span><span className="text-muted-foreground">{col.length}</span></div>
                  <div className="space-y-2 max-h-[420px] overflow-y-auto">
                    {col.map(o => (
                      <div key={o.id} className="rounded border bg-background p-2 text-xs space-y-0.5 cursor-pointer" data-testid={`pipe-os-${o.numero}`} onClick={() => setExpPipe(expPipe === o.id ? null : o.id)}>
                        <div className="font-semibold">OS {o.numero}</div>
                        <div className="truncate" title={o.cliente}>{o.cliente}</div>
                        <div className="text-muted-foreground">{fmtData(o.data)}</div>
                        {o.proximo_followup && <div className={o.proximo_followup < hoje ? 'text-red-600 font-semibold' : 'text-muted-foreground'} data-testid={`pipe-fu-${o.numero}`}>Follow-up: {fmtData(o.proximo_followup)}{o.followup_hora ? ` ${o.followup_hora}` : ''}{o.proximo_followup < hoje ? ' (vencido)' : ''}</div>}
                        {!valoresOcultos && <div className="font-medium text-emerald-700 dark:text-emerald-400">{o.cortesia ? <span className="text-[10px] rounded bg-pink-100 text-pink-700 px-1.5 py-0.5 font-bold">CORTESIA</span> : fmtBRL(o.total)}</div>}
                        {expPipe === o.id && (
                          <div className="pt-1 mt-1 border-t space-y-0.5" data-testid={`pipe-os-detalhe-${o.numero}`}>
                            {!valoresOcultos && <>
                              <div>Pagamento: {PAGAMENTOS[o.pagamento] || '-'}</div>
                              <div>Recebido: {fmtBRL(recebTotal(o))} • Pendente: {fmtBRL(pendenteDe(o))}</div>
                              <div>NF: {o.nf_numero || (o.nota_emitida ? 'emitida' : 'não')} • Contrato: {o.contrato ? 'sim' : 'não'}</div>
                              {o.obs_adm && <div className="text-muted-foreground">Obs: {o.obs_adm}</div>}
                            </>}
                            {(o.contatos || []).slice(-2).map(c => <div key={c.id} className="text-muted-foreground">Contato ({c.autor}): {c.texto}</div>)}
                            {(o.contatos || []).length === 0 && valoresOcultos && <div className="text-muted-foreground italic">sem contatos registrados</div>}
                          </div>
                        )}
                      </div>
                    ))}
                    {col.length === 0 && <p className="text-[11px] text-muted-foreground italic">vazio</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}

      {aba === 'calendario' && (() => {
        const infoDia = diaSel ? (porDia[diaSel] || { orc: [], ev: [] }) : null;
        const sugDia = diaSel ? sugestoes.filter(s => (s.data || '').slice(0, 10) === diaSel) : [];
        const prospMes = eventos.filter(e => e.data?.startsWith(mesStr));
        const sugMes = sugestoes.filter(s => (s.data || '').startsWith(mesStr));
        const SugCard = ({ s }) => (
          <div className="rounded border border-purple-200 dark:border-purple-900 bg-purple-50/40 dark:bg-purple-900/10 p-2 text-xs space-y-1" data-testid={`sug-${s.id}`}>
            <div className="font-medium">{s.nome}</div>
            <div className="text-muted-foreground">{fmtData(s.data)} {s.local && `• ${s.local}`} {s.organizador && `• ${s.organizador}`}</div>
            {(s.telefone || s.site) && <div className="text-muted-foreground">{s.telefone} {s.site && <a href={s.site.startsWith('http') ? s.site : `https://${s.site}`} target="_blank" rel="noreferrer" className="text-sky-600 dark:text-sky-400 underline break-all">{s.site.replace(/^https?:\/\//, '').slice(0, 35)}</a>}</div>}
            <div className="flex flex-wrap gap-1">
              <button className="rounded border border-emerald-400 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 font-medium" data-testid={`sug-cadastrar-${s.id}`}
                onClick={() => setFormEv({ nome: s.nome, data: (s.data || '').slice(0, 10), local: s.local || '', organizador: s.organizador || '', telefone: s.telefone || '', site: s.site || '', link_fonte: s.site || '', status_abordagem: 'prospeccao', _sugId: s.id })}>Cadastrar</button>
              {[['ignorado', 'Ignorar'], ['ja_cotado', 'Já cotado'], ['duplicado', 'Duplicado']].map(([st, lb]) => (
                <button key={st} className="rounded border px-1.5 py-0.5" data-testid={`sug-${st}-${s.id}`}
                  onClick={async () => { await api.put(`/eventos-sugestoes/${s.id}`, { status: st }); carregar(); }}>{lb}</button>
              ))}
            </div>
          </div>
        );
        return (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-2">
            <div className="flex items-center gap-3">
              <Button size="sm" variant="outline" onClick={() => { setDiaSel(null); setCalRef(c => c.mes === 1 ? { ano: c.ano - 1, mes: 12 } : { ...c, mes: c.mes - 1 }); }} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="font-semibold" data-testid="cal-mes">{String(calRef.mes).padStart(2, '0')}/{calRef.ano}</span>
              <Button size="sm" variant="outline" onClick={() => { setDiaSel(null); setCalRef(c => c.mes === 12 ? { ano: c.ano + 1, mes: 1 } : { ...c, mes: c.mes + 1 }); }} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
              <span className="text-xs text-muted-foreground">verde = livre | âmbar = ocupado | ★ = prospecção | clique no dia para ver detalhes</span>
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: diasNoMes }, (_, i) => {
                const dstr = `${mesStr}-${String(i + 1).padStart(2, '0')}`;
                const info = porDia[dstr];
                const ocupado = info?.orc?.length > 0;
                const temEv = info?.ev?.length > 0;
                return (
                  <button key={dstr} data-testid={`dia-${dstr}`} onClick={() => setDiaSel(diaSel === dstr ? null : dstr)}
                    className={`rounded-lg border p-1.5 min-h-[58px] text-xs text-left ${diaSel === dstr ? 'ring-2 ring-sky-500' : ''} ${ocupado ? 'bg-amber-50 border-amber-300 dark:bg-amber-900/30 dark:border-amber-700' : 'bg-emerald-50/60 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800'}`}>
                    <div className="font-semibold">{i + 1}</div>
                    {ocupado && <div className="text-amber-700 dark:text-amber-400">{info.orc.length} OS(s)</div>}
                    {temEv && <div className="text-purple-700 dark:text-purple-400 font-medium">★ {info.ev.length}</div>}
                    {!ocupado && !temEv && <div className="text-emerald-600 dark:text-emerald-400">livre</div>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            {formEv ? (
              <FormProspeccao inicial={formEv} onSalvo={() => { setFormEv(null); carregar(); }} onCancel={() => setFormEv(null)} />
            ) : diaSel ? (
              <div className="space-y-3" data-testid="painel-dia">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-sm">Dia {fmtData(diaSel)}</h3>
                  <Button size="sm" variant="ghost" onClick={() => setDiaSel(null)} data-testid="fechar-dia">Fechar</Button>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-amber-700 dark:text-amber-400 mb-1">OSs da Tomaselli ({infoDia.orc.length})</div>
                  <div className="space-y-1.5">
                    {infoDia.orc.map(o => (
                      <div key={o.id} className="rounded border p-2 text-xs" data-testid={`dia-os-${o.numero}`}>
                        <div className="font-semibold">OS {o.numero} <span className={`rounded px-1 text-[10px] ${SITUACOES[o.situacao]?.cor || ''}`}>{SITUACOES[o.situacao]?.label}</span></div>
                        <div className="truncate" title={o.cliente}>{o.cliente}</div>
                        {!valoresOcultos && <div className="text-emerald-700 dark:text-emerald-400 font-medium">{fmtBRL(o.total)}</div>}
                      </div>
                    ))}
                    {infoDia.orc.length === 0 && <p className="text-xs text-muted-foreground italic">Nenhuma OS neste dia — data livre</p>}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-purple-700 dark:text-purple-400 mb-1">Prospecções cadastradas ({infoDia.ev.length})</div>
                  <div className="space-y-1.5">
                    {infoDia.ev.map(e => <ProspCard key={e.id} e={e} admin={!valoresOcultos} onEdit={() => setFormEv(e)} onReload={carregar} />)}
                    {infoDia.ev.length === 0 && <p className="text-xs text-muted-foreground italic">Nenhuma prospecção neste dia</p>}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-sky-700 dark:text-sky-400 mb-1">Eventos encontrados na web ({sugDia.length})</div>
                  <div className="space-y-1.5">
                    {sugDia.map(s => <SugCard key={s.id} s={s} />)}
                    {sugDia.length === 0 && <p className="text-xs text-muted-foreground italic">Nenhuma sugestão da web para este dia</p>}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-sm">Prospecção de eventos</h3>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" disabled={buscandoEv} onClick={() => buscarEventosWeb(mesStr)} data-testid="btn-buscar-eventos" title={`Busca na web eventos de ${String(calRef.mes).padStart(2, '0')}/${calRef.ano} em Manaus`}>
                      {buscandoEv ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}<span className="ml-1 hidden sm:inline">{buscandoEv ? 'Buscando...' : 'Buscar na web'}</span>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setFormEv({ data: `${mesStr}-01` })} data-testid="btn-novo-evento"><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>
                {buscandoEv && <p className="text-[11px] text-muted-foreground animate-pulse">Pesquisando eventos de {String(calRef.mes).padStart(2, '0')}/{calRef.ano} em Manaus — leva ~30s...</p>}
                {sugMes.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-xs font-semibold uppercase text-sky-700 dark:text-sky-400">Sugestões da busca ({sugMes.length}) — cadastre, ignore ou marque</div>
                    <div className="space-y-1.5 max-h-[260px] overflow-y-auto">{sugMes.map(s => <SugCard key={s.id} s={s} />)}</div>
                  </div>
                )}
                <div className="text-xs font-semibold uppercase text-purple-700 dark:text-purple-400 pt-1">Prospecções de {String(calRef.mes).padStart(2, '0')}/{calRef.ano} ({prospMes.length})</div>
                <div className="space-y-2 max-h-[420px] overflow-y-auto">
                  {prospMes.map(e => <ProspCard key={e.id} e={e} admin={!valoresOcultos} onEdit={() => setFormEv(e)} onReload={carregar} />)}
                  {prospMes.length === 0 && <p className="text-xs text-muted-foreground">Nenhuma prospecção neste mês. Use "Buscar na web" ou o botão + para cadastrar.</p>}
                </div>
              </>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
}
